export const APP_ROLES = ["owner", "manager", "operator"] as const;
export type AppRole = (typeof APP_ROLES)[number];

export type ConnectivityState = "online" | "offline" | "degraded";

export interface SyncSummary {
  pending: number;
  syncing: number;
  retryable: number;
  conflicts: number;
}

export interface WorkItem {
  id: string;
  title: string;
  kind: "feeding" | "task" | "breeding" | "water" | "health";
  tankCode?: string;
  status: "pending" | "late" | "done" | "conflict";
  dueLabel?: string;
  domainVersion?: number;
  canComplete?: boolean;
}

export interface HomeNavItem {
  id:
    | "home"
    | "today"
    | "feeding"
    | "hall-menu"
    | "patrol"
    | "offline"
    | "workers"
    | "finance"
    | "subscribers"
    | "reports"
    | "settings";
  label: string;
  icon: string;
}

const OPERATOR_NAV: readonly HomeNavItem[] = [
  { id: "home", label: "خانه", icon: "⌂" },
  { id: "today", label: "کار امروز", icon: "✓" },
  { id: "hall-menu", label: "منوی سالن", icon: "☰" },
  { id: "patrol", label: "گشت سالن", icon: "▣" },
];

const MANAGER_CORE_NAV: readonly HomeNavItem[] = [{ id: "finance", label: "حساب‌وکتاب", icon: "﷼" }];

const PLATFORM_OWNER_NAV: readonly HomeNavItem[] = [
  { id: "subscribers", label: "مدیریت مشترکین", icon: "♙" },
];

export function navItemsForRole(role: AppRole, isPlatformOwner = false): readonly HomeNavItem[] {
  if (role === "operator") return OPERATOR_NAV;
  return [...OPERATOR_NAV, ...MANAGER_CORE_NAV, ...(isPlatformOwner ? PLATFORM_OWNER_NAV : [])];
}

export function roleLabel(role: AppRole): string {
  switch (role) {
    case "owner":
      return "مالک";
    case "manager":
      return "مدیر";
    case "operator":
      return "کارگر سالن";
  }
}

export function syncStatusLabel(connectivity: ConnectivityState, summary: SyncSummary): string {
  if (summary.conflicts > 0) {
    return `${String(summary.conflicts)} مورد نیاز به بررسی`;
  }

  if (connectivity === "offline") {
    return summary.pending > 0
      ? `${String(summary.pending)} کار در صف آفلاین`
      : "آفلاین — آماده ثبت محلی";
  }

  if (summary.retryable > 0) {
    return `${String(summary.retryable)} مورد در انتظار ارسال مجدد`;
  }

  if (summary.syncing > 0) {
    return "در حال همگام‌سازی";
  }

  if (summary.pending > 0) {
    return `${String(summary.pending)} کار در صف ارسال`;
  }

  return connectivity === "degraded" ? "اتصال ضعیف — ثبت محلی فعال" : "همگام";
}

export function syncStatusTone(
  connectivity: ConnectivityState,
  summary: SyncSummary,
): "ok" | "warning" | "danger" {
  if (summary.conflicts > 0) {
    return "danger";
  }

  if (connectivity !== "online" || summary.pending > 0 || summary.retryable > 0) {
    return "warning";
  }

  return "ok";
}

export function visibleWorkItems(items: readonly WorkItem[]): WorkItem[] {
  return [...items]
    .filter((item) => item.status !== "done")
    .sort((left, right) => {
      const rank = { conflict: 0, late: 1, pending: 2, done: 3 } as const;
      return rank[left.status] - rank[right.status];
    });
}
